import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseAdmin } from './supabase-admin';

export type NotificationRecord = {
  id: string;
  user_id: string;
  type: string | null;
  title: string | null;
  body: string | null;
  data: Record<string, any> | null;
  is_read: boolean;
  created_at: string;
};

class NotificationService {
  private supabase: SupabaseClient;

  constructor(client: SupabaseClient) {
    this.supabase = client;
  }

  async createNotification(input: {
    user_id: string;
    type?: string;
    title: string;
    body: string;
    data?: Record<string, any>;
    is_read?: boolean;
    is_admin_alert?: boolean;
  }): Promise<NotificationRecord | null> {
    const { data, error } = await this.supabase
      .from('notifications')
      .insert({
        user_id: input.user_id,
        type: input.type || 'general',
        title: input.title,
        body: input.body,
        data: input.data || {},
        is_read: input.is_read ?? false,
      })
      .select()
      .single();

    if (error) {
      console.error('createNotification error:', error);
      return null;
    }

    const notification = data as NotificationRecord;

    // Send email for important notifications
    try {
      if (input.is_admin_alert) {
        // Dynamically import to avoid client leaks
        const { EmailService } = await import('../../lib/email-service');
        const emailProvider = EmailService.getInstance();

        // For deposits, we want a richer alert if possible
        if (input.type === 'deposit_initiated') {
          const { data: user } = await this.supabase
            .from('users')
            .select('email, first_name, last_name')
            .eq('id', input.user_id)
            .single();

          const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'A User';
          const userEmail = user?.email || 'Unknown Email';

          const template = emailProvider.getAdminDepositAlertTemplate(
            userName,
            userEmail,
            input.data?.amount?.toString() || '0',
            input.data?.currency || 'USD',
            input.data?.transaction_id || '',
            input.data?.provider || 'Unknown'
          );
          await emailProvider.notifyAdmins(template);
        } else {
          await emailProvider.notifyAdmins({
            subject: `Admin Alert: ${notification.title}`,
            html: `
              <div style="font-family: sans-serif; padding: 20px; color: #1e293b;">
                <h2 style="color: #ef4444; border-bottom: 2px solid #fee2e2; padding-bottom: 10px;">🔴 Administrative Alert</h2>
                <p><strong>Event:</strong> ${notification.title}</p>
                <div style="background: #f8fafc; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0; margin: 20px 0;">
                  <p style="margin: 0;">${notification.body}</p>
                </div>
                <a href="${process.env.NEXT_PUBLIC_APP_URL}/admin" style="display: inline-block; padding: 12px 24px; background: #2563eb; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600;">Open Admin Dashboard</a>
              </div>
            `,
            text: `Admin Alert: ${notification.title}\n\n${notification.body}`
          });
        }
      } else {
        await this.sendEmailForNotification(notification, input.data);
      }
    } catch (emailError) {
      console.error('Failed to send email notification:', emailError);
    }

    return notification;
  }

  async listUserNotifications(user_id: string, limit = 50): Promise<NotificationRecord[]> {
    const { data, error } = await this.supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user_id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('listUserNotifications error:', error);
      return [];
    }

    return (data || []) as NotificationRecord[];
  }

  async markRead(user_id: string, notificationId: string): Promise<boolean> {
    const { error } = await this.supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user_id)
      .eq('id', notificationId);

    if (error) {
      console.error('markRead error:', error);
      return false;
    }

    return true;
  }

  async markAllRead(user_id: string): Promise<boolean> {
    const { error } = await this.supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user_id);

    if (error) {
      console.error('markAllRead error:', error);
      return false;
    }

    return true;
  }

  private async sendEmailForNotification(
    notification: NotificationRecord,
    data?: Record<string, any>
  ): Promise<void> {
    // Dynamically import EmailService to prevent client-side bundling of Node.js deps (fs, etc.)
    const { EmailService: EmailProvider } = await import('../../lib/email-service');
    const emailProvider = EmailProvider.getInstance();

    // Get user email
    const { data: user, error } = await this.supabase
      .from('users')
      .select('email, first_name, last_name')
      .eq('id', notification.user_id)
      .single();

    if (error || !user?.email) {
      console.error('Failed to get user email for notification:', error);
      return;
    }

    const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';

    // Send email based on notification type
    switch (notification.type) {
      case 'transaction_completed':
      case 'investment_active':
      case 'investment_completed':
      case 'deposit_initiated':
        if (notification.type === 'deposit_initiated' || data?.type === 'deposit') {
          const amount = data?.amount?.toString() || '0';
          const currency = data?.currency || 'USD';
          const txnId = data?.transaction_id || '';

          const template = notification.type === 'deposit_initiated'
            ? emailProvider.getDepositInitiatedTemplate(userName, amount, currency, txnId)
            : emailProvider.getDepositConfirmationTemplate(userName, amount, currency, txnId);

          await emailProvider.sendEmail(user.email, template);
        } else if (data?.type === 'withdrawal') {
          const template = emailProvider.getWithdrawalConfirmationTemplate(
            userName,
            data.amount?.toString() || '0',
            data.currency || 'USD',
            data.transaction_id || ''
          );
          await emailProvider.sendEmail(user.email, template);
        } else if (data?.type === 'investment' || notification.type.startsWith('investment')) {
          const template = emailProvider.getInvestmentConfirmationTemplate(
            userName,
            data?.related_object?.property_name || 'Property Investment',
            data?.amount?.toString() || '0',
            data?.currency || 'USD',
            data?.transaction_id || data?.investment_id || ''
          );
          await emailProvider.sendEmail(user.email, template);
        }
        break;

      case 'admin_alert':
        await emailProvider.sendEmail(user.email, {
          subject: `Admin Notification: ${notification.title}`,
          html: `
            <div style="font-family: sans-serif; padding: 20px; color: #1e293b;">
              <h2 style="color: #2563eb; border-bottom: 2px solid #f1f5f9; padding-bottom: 10px;">System Notification</h2>
              <p>Hello ${user.first_name || 'Admin'},</p>
              <p>You have a new administrative alert on Cardone Capvest:</p>
              <div style="background: #f8fafc; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0; margin: 20px 0;">
                <h3 style="margin-top: 0; color: #1e293b;">${notification.title}</h3>
                <p style="margin-bottom: 0;">${notification.body}</p>
              </div>
              <a href="${process.env.NEXT_PUBLIC_APP_URL}/admin" style="display: inline-block; padding: 12px 24px; background: #2563eb; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600;">Go to Admin Panel</a>
            </div>
          `,
          text: `Admin Notification: ${notification.title}\n\n${notification.body}\n\nView at: ${process.env.NEXT_PUBLIC_APP_URL}/admin`
        });
        break;

      default:
        // For other notification types, we could add more email templates
        break;
    }
  }
}

// Create notification service instance - safely handles null for client-side
const notificationService = new NotificationService(supabaseAdmin as any);
export default notificationService;
